import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Formik } from "formik";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import {
  ArrowRightIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import * as auth from "../../store/ducks/auth.duck";
import { login, initiateGoogleSSO } from "../../crud/auth.crud";
import TwoFactorVerify from "./TwoFactorVerify";
import TwoFactorEnroll from "./TwoFactorEnroll";
import axios from "axios";
import "./Login.css";

const json = require("../../../config.json");
const urlBase = json.urlBase;

export function Login(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  // Two-factor flow: held only in memory, never persisted.
  const [mfaStep, setMfaStep] = useState("none"); // 'none' | 'verify' | 'enroll'
  const [tempToken, setTempToken] = useState("");

  // Centralized post-authentication handling, shared by password login, the
  // 2FA verify/enroll steps, and the Google SSO callback.
  const handleAuthenticated = (result) => {
    const normalizedUserDetails = {
      ...result,
      pk: result?.pk ?? result?.user_id ?? result?.id,
      account_id:
        result?.account_id ??
        result?.account?.pk ??
        result?.account?.id ??
        result?.account_pk,
      access_token: result?.access_token,
    };

    try {
      localStorage.setItem(
        "userDetails",
        JSON.stringify(normalizedUserDetails)
      );
    } catch (storageError) {
      console.warn("Unable to persist user details:", storageError);
    }

    props.login(result.access_token);
    props.fulfillUser(normalizedUserDetails);
  };

  const resetToLogin = () => {
    setMfaStep("none");
    setTempToken("");
    setErrorMessage("");
  };

  // Handle SSO callback — extract token from URL and auto-login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    // SSO now hands back an MFA-pending token instead of a session token.
    const ssoMfaRequired = params.get("requires_mfa");
    const ssoTempToken = params.get("temp_token");
    const ssoSetupRequired = params.get("mfa_setup_required");

    if (error) {
      setErrorMessage(
        errorDescription || "Google sign-in failed. Please try again."
      );
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // SSO verified identity, but 2FA is mandatory. Route into the same TOTP
    // flow as password login instead of logging in directly. Never call
    // /auth/refresh or handleAuthenticated here — the access token is only
    // issued after the second factor by TwoFactorVerify/TwoFactorEnroll.
    if (ssoMfaRequired && ssoTempToken && !isAutoLogin) {
      setIsAutoLogin(true);
      setTempToken(ssoTempToken);
      setMfaStep(ssoSetupRequired === "1" ? "enroll" : "verify");
      // Clean URL so the temp token doesn't linger in the address bar/history.
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (token && !isAutoLogin) {
      setIsAutoLogin(true);
      setLoading(true);

      // Call refresh endpoint with the SSO token to get full user data
      axios
        .get(urlBase + "/auth/refresh", {
          headers: { Authorization: "Bearer " + token },
        })
        .then(({ data: { result } }) => {
          handleAuthenticated(result);
        })
        .catch(() => {
          setLoading(false);
          setIsAutoLogin(false);
          setErrorMessage("Google sign-in failed. Please try again.");
        });

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enableLoading = () => {
    setLoading(true);
    setErrorMessage("");
  };

  const disableLoading = () => {
    setLoading(false);
  };

  return (
    <main className="crm-login" data-theme="dark" aria-label="ITP CRM sign in">
      <section
        className="crm-login__scene"
        aria-label="ITP in the field"
        style={{ backgroundImage: 'url("/media/bg/logindsa.jpg")' }}
      >
        <div className="crm-login__scene-top">
          <div className="crm-login__brand">
            <span className="crm-login__logo">
              <img src="/media/logos/logo-4.png" alt="ITP" />
            </span>
            <span className="crm-login__brand-divider" aria-hidden="true" />
            <span className="crm-login__brand-name">360</span>
          </div>
          <span className="crm-login__scene-label">
            Our people. Our difference.
          </span>
        </div>

        <div className="crm-login__story">
          <span className="crm-login__eyebrow crm-login__eyebrow--light">
            Behind every connection
          </span>
          <p className="crm-login__headline">
            We make
            <br />
            <span>it happen.</span>
          </p>
          <p className="crm-login__story-copy">
            In the field. At the desk. Moving things forward, together.
          </p>
        </div>

        <div className="crm-login__scene-footer">
          <span>ITP360</span>
          <span>The team behind the technology.</span>
        </div>
      </section>

      <section className="crm-login__panel" aria-label="Your account">
        <div className="crm-login__panel-top">
          <span className="crm-login__workspace">
            ITP <span>/</span> WORKSPACE
          </span>
          <span className="crm-login__access">
            <LockClosedIcon aria-hidden="true" /> Team access
          </span>
        </div>

        <div className="crm-login__content">
          {mfaStep === "verify" ? (
            <div className="crm-login__mfa">
              <TwoFactorVerify
                tempToken={tempToken}
                onAuthenticated={handleAuthenticated}
                onCancel={resetToLogin}
              />
            </div>
          ) : mfaStep === "enroll" ? (
            <div className="crm-login__mfa">
              <TwoFactorEnroll
                tempToken={tempToken}
                onAuthenticated={handleAuthenticated}
                onCancel={resetToLogin}
              />
            </div>
          ) : (
            <>
              <header className="crm-login__intro">
                <span className="crm-login__eyebrow">Ready when you are</span>
                <h1>
                  Let's get to work<span>.</span>
                </h1>
                <p>Welcome back. Sign in to your workspace.</p>
              </header>
              <Formik
                initialValues={{
                  email: "",
                  password: "",
                }}
                validate={(values) => {
                  const errors = {};

                  if (!values.email) {
                    errors.email = intl.formatMessage({
                      id: "AUTH.VALIDATION.REQUIRED_FIELD",
                    });
                  } else if (
                    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
                      values.email
                    )
                  ) {
                    errors.email = intl.formatMessage({
                      id: "AUTH.VALIDATION.INVALID_FIELD",
                    });
                  }

                  if (!values.password) {
                    errors.password = intl.formatMessage({
                      id: "AUTH.VALIDATION.REQUIRED_FIELD",
                    });
                  }

                  return errors;
                }}
                onSubmit={(values, { setSubmitting }) => {
                  enableLoading();
                  setTimeout(() => {
                    login(values.email, values.password)
                      .then(({ data: { result } }) => {
                        disableLoading();
                        setSubmitting(false);

                        // Password verified. 2FA is mandatory: the backend never
                        // returns a session token here, only an MFA-pending token
                        // plus whether the user still needs to enroll.
                        if (result && result.requires_mfa) {
                          setTempToken(result.temp_token);
                          setMfaStep(
                            result.mfa_setup_required ? "enroll" : "verify"
                          );
                          return;
                        }

                        handleAuthenticated(result);
                      })
                      .catch(() => {
                        disableLoading();
                        setSubmitting(false);
                        setErrorMessage(
                          "Incorrect email or password. Please try again."
                        );
                      });
                  }, 1000);
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <form
                    onSubmit={handleSubmit}
                    className="crm-login__form"
                    noValidate
                    aria-busy={loading}
                  >
                    {errorMessage && (
                      <div className="crm-login__error" role="alert">
                        <ExclamationCircleIcon aria-hidden="true" />
                        <p>{errorMessage}</p>
                      </div>
                    )}

                    <div className="crm-login__field">
                      <label htmlFor="crm-login-email">Work email</label>
                      <div className="crm-login__input-wrap">
                        <EnvelopeIcon
                          className="crm-login__input-icon"
                          aria-hidden="true"
                        />
                        <input
                          id="crm-login-email"
                          type="email"
                          name="email"
                          autoComplete="username"
                          autoCapitalize="none"
                          spellCheck={false}
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={Boolean(touched.email && errors.email)}
                          aria-describedby={
                            touched.email && errors.email
                              ? "crm-login-email-error"
                              : undefined
                          }
                          placeholder="you@itp360.com"
                          required
                        />
                      </div>
                      {touched.email && errors.email && (
                        <p
                          id="crm-login-email-error"
                          className="crm-login__field-error"
                        >
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="crm-login__field">
                      <div className="crm-login__label-row">
                        <label htmlFor="crm-login-password">Password</label>
                        <Link
                          to="/auth/forgot-password"
                          className="crm-login__forgot"
                        >
                          <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
                        </Link>
                      </div>
                      <div className="crm-login__input-wrap">
                        <LockClosedIcon
                          className="crm-login__input-icon"
                          aria-hidden="true"
                        />
                        <input
                          id="crm-login-password"
                          type={showPassword ? "text" : "password"}
                          name="password"
                          autoComplete="current-password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={Boolean(
                            touched.password && errors.password
                          )}
                          aria-describedby={
                            touched.password && errors.password
                              ? "crm-login-password-error"
                              : undefined
                          }
                          placeholder="Enter your password"
                          required
                        />
                        <button
                          className="crm-login__reveal"
                          type="button"
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          aria-pressed={showPassword}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeSlashIcon aria-hidden="true" />
                          ) : (
                            <EyeIcon aria-hidden="true" />
                          )}
                        </button>
                      </div>
                      {touched.password && errors.password && (
                        <p
                          id="crm-login-password-error"
                          className="crm-login__field-error"
                        >
                          {errors.password}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting || loading}
                      className="crm-login__submit"
                    >
                      <span>{loading ? "Signing in..." : "Sign in"}</span>
                      {loading ? (
                        <span
                          className="crm-login__spinner"
                          aria-hidden="true"
                        />
                      ) : (
                        <ArrowRightIcon aria-hidden="true" />
                      )}
                    </button>

                    <div className="crm-login__divider">
                      <span>or continue with</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => initiateGoogleSSO()}
                      disabled={isSubmitting || loading}
                      className="crm-login__google"
                    >
                      <svg
                        className="crm-login__google-icon"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      <span>Sign in with Google</span>
                    </button>
                  </form>
                )}
              </Formik>
            </>
          )}
          <div className="crm-login__security">
            <ShieldCheckIcon aria-hidden="true" />
            <span>Protected with two-factor authentication</span>
          </div>
        </div>

        <footer className="crm-login__footer">
          <p>
            By signing in, you agree to follow ITP company policies and
            procedures. Your activity may be monitored.
          </p>
          <span>© {new Date().getFullYear()} ITP360. All rights reserved.</span>
        </footer>
      </section>
    </main>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
